import type { Localized } from '../types/notice'
import type { PinIcon } from '../utils/requestPins'

/** Citizen behind the Brossard demo requests. */
export const DEMO_CITIZEN = {
  name: 'Charlotte Bouchard',
  initials: 'CB',
  email: 'charlotte.bouchard@courriel.com',
  phone: '(450) 947-0210',
}

export const CITY_SIGNATURE = 'Ville de Brossard'

export interface DemoMessage {
  from: 'ville' | 'citizen'
  /** Day heading, e.g. "24 septembre 2026". */
  day: Localized
  /** Time shown under the bubble, e.g. "24 sept., 10 h 15". */
  time: Localized
  text: Localized
  /** Citizen message read by the city. */
  readAt?: Localized
  /** City message still waiting for the citizen's answer. */
  awaitingReply?: boolean
}

export interface DemoHistoryItem {
  date: Localized
  label: Localized
  /** Last step reached is highlighted. */
  current?: boolean
}

export interface DemoAttachment {
  name: string
  size: string
  url?: string
}

export interface DemoRequestDetail {
  reqId: string
  title: Localized
  category: Localized
  service: Localized
  addressLines: string[]
  coords: [number, number]
  /** Coordinates estimated from the address (not provided). */
  coordsApprox?: boolean
  createdOn: Localized
  dueOn: Localized
  dueNote?: Localized
  /** Days open at the demo date and the SLA, for the map pin. */
  daysOpen: number
  slaDays: number
  pinIcon: PinIcon
  description: Localized
  descriptionNote?: Localized
  atnaContext?: Localized
  attachments: DemoAttachment[]
  history: DemoHistoryItem[]
  messages: DemoMessage[]
  followUp?: Localized
}

const SEPT_30: Localized = { fr: '30 septembre 2026', en: 'September 30, 2026' }

export const BROSSARD_DEMO_REQUESTS: DemoRequestDetail[] = [
  {
    reqId: 'REQ-2842',
    title: { fr: 'Nid-de-poule', en: 'Pothole' },
    category: { fr: 'Voirie et chaussée', en: 'Roads and pavement' },
    service: { fr: 'Travaux publics', en: 'Public Works' },
    addressLines: ['Boulevard Grande-Allée, près de la rue Bordeaux', 'Brossard, QC'],
    coords: [45.4529, -73.4622],
    createdOn: SEPT_30,
    dueOn: { fr: '14 octobre 2026', en: 'October 14, 2026' },
    daysOpen: 0,
    slaDays: 14,
    pinIcon: 'road',
    description: { fr: 'Nid-de-poule — Moyen (30 à 60 cm)', en: 'Pothole — Medium (30 to 60 cm)' },
    descriptionNote: {
      fr: 'Taille détectée par ATNA à partir de la photo.',
      en: 'Size detected by ATNA from the photo.',
    },
    attachments: [
      {
        name: 'photo-signalement.jpg',
        size: '1,9 Mo',
        url: 'https://cdn.magicpatterns.com/patterns/generated-images/c585eb28-7d3b-4836-bd45-054c1de544e0.jpg',
      },
    ],
    history: [{ date: { fr: '30 sept.', en: 'Sept. 30' }, label: { fr: 'Demande reçue', en: 'Request received' }, current: true }],
    messages: [],
    followUp: { fr: 'Avis par courriel et/ou SMS', en: 'Notified by email and/or SMS' },
  },
  {
    reqId: 'REQ-2790',
    title: { fr: 'Lampadaire défectueux', en: 'Faulty streetlight' },
    category: { fr: 'Éclairage public', en: 'Street lighting' },
    service: { fr: 'Travaux publics', en: 'Public Works' },
    addressLines: ['4610, rue Bourgogne', 'Brossard, QC J4W 2K1'],
    coords: [45.4483, -73.4693],
    createdOn: { fr: '23 septembre 2026', en: 'September 23, 2026' },
    dueOn: { fr: '7 octobre 2026', en: 'October 7, 2026' },
    daysOpen: 7,
    slaDays: 14,
    pinIcon: 'light',
    description: {
      fr: 'Le lampadaire devant le 4610 restait éteint la nuit, laissant le trottoir dans le noir entre les deux intersections.',
      en: 'The streetlight in front of 4610 stayed off at night, leaving the sidewalk dark between the two intersections.',
    },
    attachments: [{ name: 'lampadaire-4610-bourgogne.jpg', size: '1,6 Mo' }],
    history: [
      { date: { fr: '23 sept.', en: 'Sept. 23' }, label: { fr: 'Demande reçue', en: 'Request received' } },
      {
        date: { fr: '24 sept.', en: 'Sept. 24' },
        label: { fr: 'Transmise au service de l’éclairage', en: 'Sent to the lighting department' },
      },
      { date: { fr: '25 sept.', en: 'Sept. 25' }, label: { fr: 'En traitement', en: 'In progress' } },
      {
        date: { fr: '28 sept.', en: 'Sept. 28' },
        label: { fr: 'Intervention planifiée', en: 'Intervention scheduled' },
        current: true,
      },
    ],
    messages: [
      {
        from: 'ville',
        day: { fr: '24 septembre 2026', en: 'September 24, 2026' },
        time: { fr: '24 sept., 10 h 15', en: 'Sept. 24, 10:15 a.m.' },
        text: {
          fr: 'Bonjour, votre signalement a été transmis à l’équipe d’éclairage public. Une vérification est prévue cette semaine.',
          en: 'Hello, your report has been sent to the street lighting team. An inspection is planned this week.',
        },
      },
      {
        from: 'citizen',
        day: { fr: '25 septembre 2026', en: 'September 25, 2026' },
        time: { fr: '25 sept., 8 h 03', en: 'Sept. 25, 8:03 a.m.' },
        readAt: { fr: 'Lu le 25 sept. à 9 h 17', en: 'Read Sept. 25 at 9:17 a.m.' },
        text: {
          fr: 'Merci. Le lampadaire voisin, devant le 4620, commence aussi à clignoter depuis hier soir.',
          en: 'Thank you. The next streetlight, in front of 4620, has also started flickering since last night.',
        },
      },
      {
        from: 'ville',
        day: { fr: '26 septembre 2026', en: 'September 26, 2026' },
        time: { fr: '26 sept., 14 h 30', en: 'Sept. 26, 2:30 p.m.' },
        awaitingReply: true,
        text: {
          fr: 'Merci pour la précision. Pouvez-vous nous indiquer le numéro inscrit sur la plaque du poteau ? Il nous aide à identifier le bon circuit électrique.',
          en: 'Thanks for the detail. Could you tell us the number on the pole’s plate? It helps us identify the right electrical circuit.',
        },
      },
    ],
  },
  {
    reqId: 'REQ-2903',
    title: { fr: 'Animal trouvé', en: 'Found animal' },
    category: { fr: 'Animal errant ou trouvé', en: 'Stray or found animal' },
    service: { fr: 'Service animalier', en: 'Animal services' },
    addressLines: ['Parc Maupassant, 7000, boulevard Maupassant', 'Brossard, QC J4Z 1R5'],
    coords: [45.4352, -73.4707],
    coordsApprox: true,
    createdOn: SEPT_30,
    dueOn: { fr: '1er octobre 2026', en: 'October 1, 2026' },
    daysOpen: 0,
    slaDays: 1,
    pinIcon: 'tree',
    description: {
      fr: 'Chien trouvé sans médaille lisible près de l’aire de jeux du parc. L’animal est calme et gardé en laisse en attendant une prise en charge.',
      en: 'Dog found without a readable tag near the park’s playground. The animal is calm and kept on a leash until it is picked up.',
    },
    attachments: [{ name: 'chien-trouve-parc.jpg', size: '2,1 Mo' }],
    history: [{ date: { fr: '30 sept.', en: 'Sept. 30' }, label: { fr: 'Signalement reçu', en: 'Report received' }, current: true }],
    messages: [
      {
        from: 'ville',
        day: SEPT_30,
        time: { fr: '30 sept., 9 h 42', en: 'Sept. 30, 9:42 a.m.' },
        text: {
          fr: 'Merci pour votre signalement. Un patrouilleur animalier est assigné et vous contactera si l’animal est toujours sur place.',
          en: 'Thank you for your report. An animal control officer has been assigned and will contact you if the animal is still there.',
        },
      },
    ],
  },
  {
    reqId: 'REQ-2947',
    title: { fr: 'Bac non ramassé (Ordures)', en: 'Missed bin (Garbage)' },
    category: { fr: 'Collecte des matières résiduelles', en: 'Waste collection' },
    service: { fr: 'Travaux publics', en: 'Public Works' },
    addressLines: ['4590, rue Bourgogne', 'Brossard, QC J4W 2K1'],
    coords: [45.4507, -73.4655],
    createdOn: SEPT_30,
    dueOn: { fr: '2 octobre 2026', en: 'October 2, 2026' },
    dueNote: { fr: 'Rattrapage sous 48 h ouvrables', en: 'Make-up pickup within 48 business hours' },
    daysOpen: 0,
    slaDays: 2,
    pinIcon: 'bin',
    description: {
      fr: 'Le bac d’ordures n’a pas été ramassé lors de la collecte du jour (Secteur B, Camion 214). Le bac est resté au bord de la rue.',
      en: 'The garbage bin wasn’t picked up during today’s collection (Sector B, Truck 214). The bin was left at the curb.',
    },
    atnaContext: {
      fr: 'Le camion est passé à l’adresse vers 9 h 42 ; la citoyenne a répondu « Non, il est toujours plein ».',
      en: 'The truck passed the address around 9:42 a.m.; the citizen answered “No, it’s still full”.',
    },
    attachments: [],
    history: [
      { date: { fr: '30 sept.', en: 'Sept. 30' }, label: { fr: 'Requête ouverte par ATNA', en: 'Request opened by ATNA' } },
      {
        date: { fr: '30 sept.', en: 'Sept. 30' },
        label: { fr: 'Transmise à la Ville de Brossard', en: 'Sent to the Ville de Brossard' },
        current: true,
      },
    ],
    messages: [
      {
        from: 'ville',
        day: SEPT_30,
        time: { fr: '30 sept.', en: 'Sept. 30' },
        text: {
          fr: 'Merci pour votre signalement. Un rattrapage est prévu dans les 48 heures ouvrables. Laissez votre bac au bord de la rue d’ici là.',
          en: 'Thank you for your report. A make-up pickup is scheduled within 48 business hours. Please leave your bin at the curb until then.',
        },
      },
    ],
  },
]
